import { Component, computed, inject, input, signal } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { AgentChatService } from "../agent-chat/agent-chat.service";
import { WALKTHROUGHS_ROUTE, isPublishedWalkthrough, walkthroughById } from "./walkthrough-catalog";
import { WalkthroughLauncherService } from "./walkthrough-launcher.service";
import { WalkthroughRunnerService } from "./walkthrough-runner.service";
import { WalkthroughTaskService, type TaskStepStatus } from "./walkthrough-task.service";

const STATUS_ICON: Record<TaskStepStatus, string> = {
  done: "check_circle",
  skipped: "remove_circle_outline",
  current: "play_circle",
  pending: "radio_button_unchecked",
};

const STATUS_LABEL: Record<TaskStepStatus, string> = {
  done: "Done",
  skipped: "Skipped",
  current: "Current step",
  pending: "Not started",
};

/**
 * The chat half of a walkthrough: the step list under the assistant's reply,
 * with Start, and Back / Next / Exit while a task runs. The assistant's
 * start-walkthrough tool only names the walkthrough (and only on the
 * Walkthroughs page); nothing happens until the user presses Start, and every
 * step after that is theirs to perform.
 *
 * Start goes through WalkthroughLauncherService, which works only on the
 * Walkthroughs page. Anywhere else (a card restored from an older
 * conversation, the popout) the card offers "Open Walkthroughs" instead.
 */
@Component({
  selector: "tp-walkthrough-card",
  standalone: true,
  imports: [MatIconModule],
  template: `
    @if (entry(); as entry) {
      <section class="wt-card" [attr.aria-label]="entry.title + ' walkthrough'">
        <header class="wt-card-head">
          <mat-icon aria-hidden="true">tour</mat-icon>
          <span class="wt-card-title">{{ entry.title }}</span>
          <span class="wt-card-count">{{ entry.kind === "task" ? entry.steps.length + " steps" : "Page tour · " + entry.steps.length + " stops" }}</span>
        </header>
        <ol class="wt-card-steps">
          @for (step of entry.steps; track step.id; let i = $index) {
            @let status = statuses()[i];
            <li class="wt-card-step" [class.wt-card-step--current]="status === 'current'" [class.wt-card-step--past]="status === 'done' || status === 'skipped'">
              <mat-icon class="wt-card-step-icon" [attr.aria-label]="statusLabel[status]">{{ statusIcon[status] }}</mat-icon>
              <span class="wt-card-step-body">
                <span class="wt-card-step-title">{{ step.title }}</span>
                @if (status === "current") {
                  <span class="wt-card-step-text">{{ step.text }}</span>
                }
              </span>
            </li>
          }
        </ol>
        @if (finished() && entry.finished) {
          <p class="wt-card-finished">{{ entry.finished }}</p>
        }
        <footer class="wt-card-actions">
          @if (running()) {
            @if (currentIsManual()) {
              <button type="button" class="wt-card-btn wt-card-btn--primary" (click)="task.completeManual()">Done</button>
            }
            <button type="button" class="wt-card-btn" [disabled]="!task.canGoBack()" (click)="task.back()">Back</button>
            @if (task.showsNext()) {
              <button type="button" class="wt-card-btn" [disabled]="!task.canGoNext()" (click)="task.next()">Next</button>
            }
            <!-- Skip replaced by Next (2026-10-06). To bring it back:
            <button type="button" class="wt-card-btn" (click)="task.skip()">Skip step</button> -->
            <button type="button" class="wt-card-btn" (click)="task.exit()">Exit</button>
          } @else if (tourRunning()) {
            <span class="wt-card-note">The tour is running on the page.</span>
          } @else if (!canStartHere()) {
            <button type="button" class="wt-card-btn wt-card-btn--primary" (click)="openWalkthroughs()">Open Walkthroughs</button>
            <span class="wt-card-note">Walkthroughs start from the Walkthroughs page.</span>
          } @else {
            <button type="button" class="wt-card-btn wt-card-btn--primary" (click)="start()">
              {{ finished() ? "Run it again" : entry.kind === "task" ? "Start walkthrough" : "Start tour" }}
            </button>
            <span class="wt-card-note">{{ notice() || "Press Start and each step is highlighted on the page. You do every step yourself." }}</span>
          }
        </footer>
      </section>
    }
  `,
  styleUrls: ["./walkthrough-card.component.scss"],
})
export class WalkthroughCardComponent {
  readonly walkthroughId = input.required<string>();

  protected readonly task = inject(WalkthroughTaskService);
  protected readonly chat = inject(AgentChatService);
  private readonly launcher = inject(WalkthroughLauncherService);
  private readonly runner = inject(WalkthroughRunnerService);

  protected readonly statusIcon = STATUS_ICON;
  protected readonly statusLabel = STATUS_LABEL;
  protected readonly notice = signal("");

  /** Hidden walkthroughs (PUBLISHED_WALKTHROUGH_IDS) show no card, even from older history. */
  protected readonly entry = computed(() => {
    const entry = walkthroughById(this.walkthroughId());
    return entry && isPublishedWalkthrough(entry) ? entry : null;
  });
  protected readonly running = computed(() => this.task.active()?.entry.id === this.walkthroughId());
  protected readonly tourRunning = computed(() => this.runner.running() === this.walkthroughId());
  protected readonly finished = computed(() => this.task.finished()?.id === this.walkthroughId());
  protected readonly currentIsManual = computed(() => this.running() && this.task.currentStep()?.done?.kind === "manual");
  /** Only on the Walkthroughs page, and never from the popout (it has no page to guide). */
  protected readonly canStartHere = computed(() => this.launcher.onWalkthroughsPageSignal() && !this.chat.fullScreen());
  protected readonly statuses = computed<TaskStepStatus[]>(() => {
    const entry = this.entry();
    if (!entry) return [];
    this.task.active(); // re-read as the walkthrough advances
    return this.finished() ? entry.steps.map(() => "done" as const) : this.task.statuses(entry);
  });

  protected async start(): Promise<void> {
    const entry = this.entry();
    if (!entry) return;
    const result = await this.launcher.launch(entry);
    this.notice.set(
      result === "needs-portfolio"
        ? this.launcher.ownsNoPortfolios()
          ? "This one needs a portfolio. Create one first with a PORTFOLIO walkthrough on this page."
          : "This one works on a portfolio. Select one in the menu first."
        : result === "nothing-to-show"
          ? "There's nothing to show on that page yet."
          : "",
    );
  }

  /** Off the Walkthroughs page: go there (in the popout, a new tab). */
  protected openWalkthroughs(): void {
    this.chat.navigateAppLink(WALKTHROUGHS_ROUTE, "");
  }
}
